<template>
  <AccessStateLayout
    description="You got kicked from this conference!"
    eyebrow="Session access"
    test-id="kicked-banner"
    title="You have been removed"
  >
    <p v-if="countdownMessage" data-testid="kicked-countdown" class="rounded-2xl bg-slate-100 px-4 py-4 text-sm text-slate-700">
      {{ countdownMessage }}
    </p>

    <p
      v-if="displayErrorMessage"
      class="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700"
      role="alert"
    >
      {{ displayErrorMessage }}
    </p>

    <template v-if="roomSlug" #actions>
      <CButton
        data-testid="kicked-rejoin-button"
        size="sm"
        tone="accent"
        :disabled="!canRejoin || rejoinPending"
        @click="void handleRejoin()"
      >
        {{ kickState?.isKicked === false ? 'Return to conference' : 'Rejoin conference' }}
      </CButton>
    </template>
  </AccessStateLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useConvexMutation, useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import AccessStateLayout from './AccessStateLayout.vue';
import CButton from './primitives/CButton.vue';

type KickState = {
  isKicked: boolean;
  kickReleaseAt: number | null;
  participantId: string;
};

const props = defineProps<{
  roomSlug?: string;
}>();

const router = useRouter();
const rejoinError = ref<string | null>(null);
const rejoinPending = ref(false);
const now = ref(Date.now());
let countdownHandle: number | null = null;

const queryArgs = computed(() => ({
  conferenceSlug: props.roomSlug ?? '',
}));
const kickedConferenceQuery = props.roomSlug ? useConvexQuery(api.participants.getKickState, queryArgs) : null;
const { mutate: rejoinAfterKick } = useConvexMutation(api.participants.rejoinAfterKick);

const kickState = computed(() => (kickedConferenceQuery?.data.value as KickState | null | undefined) ?? null);
const queryErrorMessage = computed(() => kickedConferenceQuery?.error.value?.message ?? null);
const kickReleaseAt = computed(() => kickState.value?.kickReleaseAt ?? null);
const remainingMs = computed(() => {
  if (kickReleaseAt.value === null) {
    return 0;
  }

  return Math.max(0, kickReleaseAt.value - now.value);
});
const canRejoin = computed(() => {
  if (!props.roomSlug || !kickState.value) {
    return false;
  }

  if (!kickState.value.isKicked) {
    return true;
  }

  return remainingMs.value === 0;
});

const countdownMessage = computed(() => {
  if (!props.roomSlug) {
    return null;
  }

  if (!kickState.value) {
    return 'Checking your rejoin window for this conference.';
  }

  if (kickState.value.isKicked === false) {
    return 'Use the button below when you want to go back.';
  }

  if (kickReleaseAt.value === null) {
    return 'Your conference access is being updated.';
  }

  if (remainingMs.value === 0) {
    return 'Use the button below when you want to go back.';
  }

  return `Remaining time: ${formatCountdown(remainingMs.value)}.`;
});
const displayErrorMessage = computed(() => rejoinError.value ?? queryErrorMessage.value);

watch(
  () => kickReleaseAt.value,
  (nextKickReleaseAt) => {
    stopCountdown();
    now.value = Date.now();

    if (!nextKickReleaseAt || nextKickReleaseAt <= now.value || typeof window === 'undefined') {
      return;
    }

    countdownHandle = window.setInterval(() => {
      now.value = Date.now();

      if (nextKickReleaseAt <= now.value) {
        stopCountdown();
      }
    }, 1_000);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  stopCountdown();
});

async function handleRejoin(): Promise<void> {
  if (!props.roomSlug || !canRejoin.value || rejoinPending.value) {
    return;
  }

  rejoinError.value = null;
  rejoinPending.value = true;

  try {
    if (kickState.value?.isKicked !== false) {
      await rejoinAfterKick({
        conferenceSlug: props.roomSlug,
      });
    }

    await router.replace({
      name: 'conference',
      params: {
        roomSlug: props.roomSlug,
      },
    });
  } catch (error) {
    rejoinError.value = error instanceof Error ? error.message : 'Unable to rejoin this conference yet.';
  } finally {
    rejoinPending.value = false;
  }
}

function stopCountdown(): void {
  if (countdownHandle !== null) {
    window.clearInterval(countdownHandle);
    countdownHandle = null;
  }
}

function formatCountdown(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
</script>
